import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, eur, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, XCircle, Printer, RefreshCcw, AlertTriangle, FlaskConical } from "lucide-react";

// Default intent template: RawBT is the reliable Android bridge for ESC/POS.
// It accepts base64-encoded ESC/POS payloads via a custom URL scheme.
// The Epson TM apps (TM Utility / TM Print Assistant) DO NOT accept intents —
// they are configuration tools only, not print bridges.
const DEFAULT_INTENT = "rawbt:base64,{base64}";
const POLL_MS = 2000;

// --- ESC/POS builder --------------------------------------------------
const ESC = 0x1b, GS = 0x1d;

// CP858 (Western Europe + Euro) encoding table for chars we use in Italian.
// Epson TM printers default to CP437 → set to CP858 with "ESC t 19" at init.
const CP858 = {
  "à": 0x85, "á": 0xA0, "â": 0x83, "ä": 0x84,
  "è": 0x8A, "é": 0x82, "ê": 0x88, "ë": 0x89,
  "ì": 0x8D, "í": 0xA1, "î": 0x8C, "ï": 0x8B,
  "ò": 0x95, "ó": 0xA2, "ô": 0x93, "ö": 0x94,
  "ù": 0x97, "ú": 0xA3, "û": 0x96, "ü": 0x81,
  "ç": 0x87, "ñ": 0xA4,
  "À": 0xB7, "Á": 0xB5, "Â": 0xB6, "Ä": 0x8E,
  "È": 0xD4, "É": 0x90, "Ê": 0xD2, "Ë": 0xD3,
  "Ì": 0xDE, "Í": 0xD6, "Î": 0xD7, "Ï": 0xD8,
  "Ò": 0xE3, "Ó": 0xE0, "Ô": 0xE2, "Ö": 0x99,
  "Ù": 0xEB, "Ú": 0xE9, "Û": 0xEA, "Ü": 0x9A,
  "Ç": 0x80, "Ñ": 0xA5,
  "€": 0xD5, "£": 0x9C, "°": 0xF8,
};
function encodeCP858(str) {
  const out = [];
  for (const ch of str) {
    const code = ch.charCodeAt(0);
    if (code < 0x80) { out.push(code); continue; }        // ASCII
    const mapped = CP858[ch];
    if (mapped != null) out.push(mapped);
    else out.push(0x3F);                                   // '?' fallback
  }
  return out;
}
function b(...arr) {
  return Uint8Array.from(arr.flatMap((x) => (typeof x === "number" ? [x] : encodeCP858(x))));
}
function concat(chunks) {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) { out.set(c, off); off += c.length; }
  return out;
}
function toBase64(u8) {
  let s = "";
  for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
  return btoa(s);
}
function line(txt = "") { return b(txt + "\n"); }
function dashes(w = 32) { return b("-".repeat(w) + "\n"); }
function centered() { return b(ESC, 0x61, 0x01); }   // center align
function leftAlign() { return b(ESC, 0x61, 0x00); }
function bold(on) { return b(ESC, 0x45, on ? 1 : 0); }
function bigSize(on) { return b(ESC, 0x21, on ? 0x30 : 0x00); } // double W+H
function feed(n) { return b(ESC, 0x64, n); }
function cut() { return b(GS, 0x56, 0x00); }
function initPrinter() { return b(ESC, 0x40, ESC, 0x74, 19); }  // reset + set codepage CP858

function buildOrderTicket(o) {
  const now = new Date(o.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  const parts = [
    initPrinter(),
    centered(),
    bigSize(true), line("SARABI BEACH"), bigSize(false),
    line(`Ordine ${o.code}`),
    line(now),
    dashes(),
    bigSize(true), line(o.station_name), bigSize(false),
    line(`Cliente: ${o.customer_name}`),
    line(o.customer_phone || ""),
    dashes(),
    leftAlign(),
  ];
  for (const it of o.items) {
    parts.push(bold(true), line(`${it.quantity}x ${it.product_name}`), bold(false));
    for (const m of it.modifiers || []) parts.push(line(`  - ${m.option_name}`));
    if (it.note) parts.push(line(`  Nota: ${it.note}`));
    if (it.allergens?.length) parts.push(line(`  Allergeni: ${it.allergens.join(", ")}`));
  }
  parts.push(
    dashes(),
    line(`Subtotale:    ${(o.subtotal_cents / 100).toFixed(2)} €`),
  );
  if (o.service_charge_cents) parts.push(line(`Servizio:     ${(o.service_charge_cents / 100).toFixed(2)} €`));
  parts.push(
    bold(true), line(`TOTALE:       ${(o.total_cents / 100).toFixed(2)} €`), bold(false),
    line(`Pagamento: ${o.payment_method} (${o.payment_status})`),
    feed(2), cut()
  );
  return concat(parts);
}

// --- Component --------------------------------------------------------
export default function PrintStation() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [queue, setQueue] = useState([]);
  const [lastPrinted, setLastPrinted] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | ok | error
  const [statusMsg, setStatusMsg] = useState("In attesa di ordini");
  const [enabled, setEnabled] = useState(false); // user must arm autoprint (browser gesture)
  const [intentTpl, setIntentTpl] = useState(() => localStorage.getItem("sb_print_intent") || DEFAULT_INTENT);
  const dispatchingRef = useRef(false);
  const knownIdsRef = useRef(new Set());

  // Persist the intent template
  useEffect(() => { localStorage.setItem("sb_print_intent", intentTpl); }, [intentTpl]);

  const dispatchPrint = useCallback((order) => {
    try {
      const bytes = buildOrderTicket(order);
      const b64 = toBase64(bytes);
      const url = intentTpl
        .replace("{base64}", encodeURIComponent(b64))
        .replace("{{base64}}", encodeURIComponent(b64));
      // Trigger the Android bridge app via intent URL
      console.log("[PrintStation] Dispatching intent for order", order.code, "url length:", url.length);
      window.location.href = url;
      return true;
    } catch (e) {
      console.error("dispatch print failed", e);
      return false;
    }
  }, [intentTpl]);

  const printTest = useCallback(() => {
    const fakeOrder = {
      id: "TEST",
      code: "TEST-" + new Date().toISOString().slice(11, 19).replace(/:/g, ""),
      created_at: new Date().toISOString(),
      station_name: "Postazione di Prova",
      customer_name: "Cliente Test",
      customer_phone: "+39 000 000 0000",
      items: [
        { quantity: 1, product_name: "Espresso", modifiers: [], note: "Test scontrino", allergens: [] },
        { quantity: 2, product_name: "Spritz Aperol", modifiers: [{ option_name: "con oliva" }], note: null, allergens: [] },
      ],
      subtotal_cents: 1750,
      service_charge_cents: 0,
      total_cents: 1750,
      payment_method: "test",
      payment_status: "n/a",
    };
    setStatus("idle"); setStatusMsg("Invio scontrino di prova alla stampante…");
    const ok = dispatchPrint(fakeOrder);
    setTimeout(() => {
      if (ok) {
        setLastPrinted(fakeOrder);
        setStatus("ok"); setStatusMsg("Scontrino di prova inviato — controlla la stampante");
      } else {
        setStatus("error"); setStatusMsg("Errore nella creazione dello scontrino di prova");
      }
    }, 800);
  }, [dispatchPrint]);

  const markOnServer = useCallback(async (orderId, success) => {
    try {
      await api.post(`/staff/orders/${orderId}/print-attempt?success=${success}`);
    } catch { /* ignore, will retry next tick */ }
  }, []);

  const processQueue = useCallback(async () => {
    if (!enabled || dispatchingRef.current || queue.length === 0) return;
    dispatchingRef.current = true;
    const order = queue[0];
    setStatus("idle"); setStatusMsg(`Invio stampa ordine ${order.code}...`);
    const ok = dispatchPrint(order);
    // We can't reliably know from the browser if the printer accepted the job.
    // Optimistic: mark as printed after 1.5s (job left the browser). Any real
    // hardware error can be inspected via the "Ristampa ultimo ordine" button.
    setTimeout(async () => {
      await markOnServer(order.id, ok);
      if (ok) {
        setLastPrinted(order);
        setStatus("ok"); setStatusMsg(`Stampato ordine ${order.code}`);
        setQueue((q) => q.filter((x) => x.id !== order.id));
      } else {
        setStatus("error"); setStatusMsg(`Errore stampa ordine ${order.code} — riprovo`);
      }
      dispatchingRef.current = false;
    }, 1500);
  }, [enabled, queue, dispatchPrint, markOnServer]);

  // Poll unprinted every 2s
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/staff/orders/unprinted");
        if (cancelled) return;
        setOrders(data);
        // Compute new orders using ref as source of truth. Mutate the ref OUTSIDE
        // the setState callback (React 18 StrictMode invokes updaters twice in
        // DEV, so any side-effect inside would run twice and break dedup).
        const toAdd = data.filter((o) => !knownIdsRef.current.has(o.id));
        if (toAdd.length > 0) {
          toAdd.forEach((o) => knownIdsRef.current.add(o.id));
          setQueue((q) => [...q, ...toAdd]);
        }
      } catch (e) { /* silent */ }
    };
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Process queue whenever it or enabled changes
  useEffect(() => { processQueue(); }, [processQueue]);

  const reprintLast = () => {
    if (!lastPrinted) return;
    setStatus("idle"); setStatusMsg(`Ristampa ordine ${lastPrinted.code}...`);
    dispatchPrint(lastPrinted);
  };

  const enableAutoprint = () => {
    // Must be a user gesture the first time; some intent:// URIs need it
    setEnabled(true);
    setStatus("ok"); setStatusMsg("Autoprint attivo");
  };

  return (
    <div className="min-h-screen bg-ink-900 text-sand-100 p-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow text-bronze-400">Sarabi Beach</div>
          <h1 className="font-serif text-3xl mt-1">Stazione Stampa</h1>
          <div className="text-xs opacity-70 mt-1">Tenere questa pagina aperta sul tablet. Polling ogni {POLL_MS/1000}s.</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusPill status={status} enabled={enabled} />
          <Button onClick={printTest} data-testid="print-test-btn" variant="outline" className="rounded-full !bg-white !text-ink-900 hover:!bg-sand-100">
            <FlaskConical className="w-4 h-4 mr-1" /> Stampa test
          </Button>
          {!enabled ? (
            <Button onClick={enableAutoprint} data-testid="enable-autoprint-btn" className="rounded-full">
              <Printer className="w-4 h-4 mr-1" /> Attiva autoprint
            </Button>
          ) : (
            <Button onClick={reprintLast} disabled={!lastPrinted} variant="outline" data-testid="reprint-last-btn" className="rounded-full text-ink-900">
              <RefreshCcw className="w-4 h-4 mr-1" /> Ristampa ultimo
            </Button>
          )}
          <div className="text-xs opacity-70">{user?.email}</div>
          <Button variant="outline" size="sm" onClick={logout} className="rounded-full !bg-white !text-ink-900 hover:!bg-sand-100">Esci</Button>
        </div>
      </header>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <section className="rounded-2xl bg-black/40 border border-white/10 p-5">
          <div className="eyebrow text-bronze-400">Stato</div>
          <div className="mt-2 text-lg" data-testid="print-status-msg">{statusMsg}</div>
          <div className="mt-3 text-sm opacity-70">
            Coda: <b data-testid="queue-count">{queue.length}</b> · Non stampati (server): <b>{orders.length}</b>
          </div>
          <div className="mt-4">
            <Label className="text-xs opacity-70">Intent stampante (avanzato)</Label>
            <Input value={intentTpl} onChange={(e) => setIntentTpl(e.target.value)}
              data-testid="intent-template" className="mt-1 !bg-white !text-ink-900 placeholder:text-ink-400" />
            <div className="text-[11px] opacity-60 mt-1">
              Default: <b>RawBT</b> (universal ESC/POS bridge — installalo dal Play Store).
              Placeholder: <code>{"{base64}"}</code>.
            </div>
          </div>
          <details className="mt-4 rounded-xl bg-white/5 border border-white/10 p-3 text-xs opacity-80">
            <summary className="cursor-pointer opacity-100 font-medium">Guida rapida configurazione tablet</summary>
            <ol className="list-decimal ml-4 mt-2 space-y-1">
              <li>Collega la stampante Epson al tablet Samsung via cavo USB (usa l'adattatore USB-C OTG se necessario) e accendila.</li>
              <li>Installa <b>RawBT</b> dal Play Store (pacchetto <code>ru.a402d.rawbtprinter</code>). È un ponte universale che accetta ESC/POS via URL <code>rawbt:</code>.</li>
              <li>Apri RawBT → Settings → Connection = <b>USB</b> → seleziona la stampante Epson dal menù → tocca <b>Test print</b>: deve uscire uno scontrino di prova dall'app.</li>
              <li>(Facoltativo) Installa <b>Epson TM Utility</b> dal Play Store solo per configurare la stampante (es. taglierina, larghezza rullo). Non serve per stampare.</li>
              <li>Apri questa pagina (<code>/print-station</code>) su Chrome del tablet e aggiungila alla schermata Home per usarla a schermo intero.</li>
              <li>Tocca <b>Stampa test</b> qui: Chrome apre RawBT via intent <code>rawbt:base64,...</code> → esce lo scontrino di prova.</li>
              <li>Se il template default non funziona per il tuo hardware, puoi personalizzarlo nel campo "Intent stampante". Chiedimi supporto per casi speciali.</li>
              <li>Una volta funzionante, tocca <b>Attiva autoprint</b>: ogni nuovo ordine cliente sarà stampato automaticamente entro 2 secondi.</li>
            </ol>
          </details>
        </section>

        <section className="rounded-2xl bg-black/40 border border-white/10 p-5">
          <div className="eyebrow text-bronze-400">Ultimo ordine stampato</div>
          {lastPrinted ? (
            <div className="mt-2">
              <div className="font-serif text-2xl">{lastPrinted.station_name}</div>
              <div className="text-sm opacity-80">Codice: {lastPrinted.code}</div>
              <div className="text-sm opacity-80">Totale: {eur(lastPrinted.total_cents)}</div>
              <ul className="mt-2 text-sm">
                {lastPrinted.items.map((i, idx) => <li key={idx}>{i.quantity}× {i.product_name}</li>)}
              </ul>
            </div>
          ) : <div className="opacity-60 text-sm mt-2">Nessuno stampato in questa sessione.</div>}
        </section>
      </div>

      <section className="mt-4 rounded-2xl bg-black/40 border border-white/10 p-5">
        <div className="eyebrow text-bronze-400">Coda di stampa</div>
        {queue.length === 0 ? (
          <div className="opacity-60 text-sm mt-2">Nessun ordine in coda.</div>
        ) : (
          <ul className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {queue.map((o) => (
              <li key={o.id} data-testid={`queue-${o.id}`} className="rounded-xl bg-white/5 border border-white/10 p-3">
                <div className="font-serif text-xl">{o.station_name}</div>
                <div className="text-xs opacity-70">{o.code} · {o.items.length} righe</div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function StatusPill({ status, enabled }) {
  const style = !enabled
    ? "bg-yellow-500/20 text-yellow-200 border-yellow-500/40"
    : status === "ok" ? "bg-emerald-500/20 text-emerald-200 border-emerald-500/40"
    : status === "error" ? "bg-red-500/20 text-red-200 border-red-500/40"
    : "bg-white/10 text-sand-100 border-white/20";
  const Icon = !enabled ? AlertTriangle : status === "ok" ? CheckCircle2 : status === "error" ? XCircle : Printer;
  const label = !enabled ? "Autoprint disattivo"
    : status === "ok" ? "Stampante pronta"
    : status === "error" ? "Errore ultima stampa"
    : "In attesa";
  return (
    <span data-testid="printer-status-pill" className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border ${style}`}>
      <Icon className="w-4 h-4" /> {label}
    </span>
  );
}
